import { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { Button, Center, Column, CustomSpinner, Row, Text } from '~/commonComponents';
import {
  AutoSaveStatus,
  BasicInfoForm,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import { firePermPostJobBasicInfoSubmitted } from '~/posthogs/events';

export const BasicInformationPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);

  const generalJobInfoRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.GeneralJobInformation]: generalJobInfoRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.WorkDetails,
    triggers: ['jobTitle', 'office', 'officeSpecialty', 'professionType'],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobBasicInfoSubmitted({
        postingId,
        professionType: formData.professionType,
      });
    },
  });
  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  if (isLoading) {
    return (
      <Center marginBlock={48}>
        <CustomSpinner isLoading size={24} />
      </Center>
    );
  }

  return (
    <>
      <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
      <Column width="60%" alignSelf="center">
        <Text bold fontSize={22} marginBottom={14} marginLeft={24}>
          Step 1: Basic Information
        </Text>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <InfoSection
            ref={generalJobInfoRef}
            icon="briefcase-solid"
            title="General Job Information"
          >
            <BasicInfoForm />
          </InfoSection>
        </FormProvider>
        <Row justifyContent="center" marginTop={24}>
          <Button text="Next" onClick={onSave} isLoading={isPending || isAutoSaveLoading} />
        </Row>
      </Column>
    </>
  );
};
